/* The onboarding wizard, copied from `portable/app/CodeNerveApp.jsx` (its first 1274 lines).

   This is the prototype's file, not a reimplementation of it. Five things are rebound
   because they reached outside that file, and nothing else is touched:

     - `CN.user` / `CN.profile` become props, so the wizard opens on the real account.
     - `GitMark` imports its artwork instead of pointing at a relative path.
     - `openDocs`, which set `window.location`, becomes a prop.
     - `DASH_STEP` mounted the prototype's own fake dashboard; here "Finish" hands back to
       the caller, which saves the answers and routes into the real one.
     - The `window` registration at the end becomes an export.

   Everything else -- the backdrop, the wire spheres, the repository graph, the four
   agent-config tabs and their live previews, the invite, trial and ready screens -- is
   the prototype's own code. Edit it by editing this file; it has diverged from the
   prototype the moment either changes, which is the trade for having it run against a
   real API. */

import React from "react";

import githubMark from "@ds/assets/logos/mark-github.png";

/* app.codenerve.ai. PRE-LAUNCH EARLY ACCESS.
   Same app as CodeNerveApp.full.jsx (app-full.html), with two content changes:
   step 01 is an application form, and the last step thanks the applicant instead of
   handing off into the dashboard. Everything between is untouched.

   app.codenerve.ai, the CodeNerve product app, one file.
   Merged from the onboarding wizard and the app shell so the app ships independently
   of the marketing site: it loads no site components and shares no state with it. */

const MARK_SPOKES = [[34, 0, 85, 0], [29.44, 17, 73.61, 42.5], [17, 29.44, 42.5, 73.61], [0, 34, 0, 85], [-17, 29.44, -42.5, 73.61], [-29.44, 17, -73.61, 42.5], [-34, 0, -85, 0], [-29.44, -17, -73.61, -42.5], [-17, -29.44, -42.5, -73.61], [0, -34, 0, -85], [17, -29.44, 42.5, -73.61], [29.44, -17, 73.61, -42.5]];

function LogoMark({ size = 26, color = "currentColor", style }) {
  return (
    <svg viewBox="-100 -100 200 200" width={size} height={size} preserveAspectRatio="xMidYMid meet" aria-hidden="true" style={{ display: "block", flex: "0 0 auto", ...style }}>
      <g fill={color} stroke={color}>
        <circle r="24" fill="none" strokeWidth="20"></circle>
        <g strokeWidth="9" strokeLinecap="round">
          {MARK_SPOKES.map(([x1, y1, x2, y2]) => <line key={x2 + ":" + y2} x1={x1} y1={y1} x2={x2} y2={y2}></line>)}
        </g>
        {MARK_SPOKES.map(([, , x, y]) => <circle key={"d" + x + ":" + y} cx={x} cy={y} r="7.5" stroke="none"></circle>)}
      </g>
    </svg>
  );
}

const BG_SPHERES = [sphere(58, 0.4), sphere(46, 2.1)];

function WireSphere({ data, size, spin, reverse, style }) {
  const px = q => [50 + q[0] * 46, 50 - q[1] * 46];
  return (
    <span style={{ display: "block", width: size, aspectRatio: "1", ...style }}>
      <span style={{ display: "block", width: "100%", height: "100%", animation: "cn-turn " + spin + "s linear infinite" + (reverse ? " reverse" : ""), transformOrigin: "50% 50%" }}>
        <svg viewBox="0 0 100 100" style={{ width: "100%", height: "100%", display: "block", overflow: "visible" }}>
          {data.edges.map(([i, j], k) => {
            const a = px(data.pts[i]), b = px(data.pts[j]);
            const depth = (data.pts[i][2] + data.pts[j][2]) / 2;
            return <line key={k} x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} stroke="var(--graph-line)" strokeWidth={depth > 0 ? 0.34 : 0.2} opacity={0.1 + (depth + 1) * 0.16} />;
          })}
          {data.pts.map((q, i) => {
            const c = px(q);
            return <circle key={i} cx={c[0]} cy={c[1]} r={q[2] > 0 ? 0.85 : 0.55} fill="var(--graph-node)" opacity={0.2 + (q[2] + 1) * 0.24} />;
          })}
        </svg>
      </span>
    </span>
  );
}

function BgSpheres() {
  return (
    <>
      <WireSphere data={BG_SPHERES[0]} size="min(40vw,470px)" spin={46} style={{ position: "absolute", right: "3%", top: "5%", opacity: 0.8 }} />
      <WireSphere data={BG_SPHERES[1]} size="min(33vw,390px)" spin={58} reverse style={{ position: "absolute", left: "2%", bottom: "4%", opacity: 0.7 }} />
    </>
  );
}

/* Flowing monochrome ribbons share the spheres' stroke and opacity range. */
const WAVE_PATHS = Array.from({ length: 36 }, (_, strand) => {
  const offset = (strand - 17.5) / 17.5;
  return Array.from({ length: 121 }, (_, point) => {
    const x = point * 14 - 120;
    const y = 180 + x * 0.34 + Math.sin(x / 160) * 105
      + offset * (70 + 45 * Math.cos(x / 220)) * Math.cos(x / 280)
      + Math.sin(x / 210 + offset * 1.2) * 48;
    return `${point ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
});

function AppBackdrop({ step = -1, dense = false }) {
  return (
    <div aria-hidden="true" data-onboarding-backdrop={dense ? "minimal" : "neutral"} style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", background: dense ? "radial-gradient(ellipse at 50% 0%, #ffffff04, transparent 65%)" : "radial-gradient(ellipse at 10% 35%, #ffffff09, transparent 45%), radial-gradient(ellipse at 90% 65%, #ffffff07, transparent 45%)" }}>
      <style>{`
        .cn-wave-art { position: absolute; inset: -5%; mask-image: linear-gradient(90deg,#000 8%,#000b 25%,transparent 37%,transparent 63%,#000b 75%,#000 92%); }
        .cn-wave-art--minimal { opacity: .24; }
        /* Each layer is its own <svg>, moved as a whole: the GPU slides the drawn lines
           instead of the browser re-rasterising every path on every frame, which stuttered. */
        .cn-wave-flow { position: absolute; inset: 0; width: 100%; height: 100%; will-change: transform; animation: cn-mesh-flow 22s ease-in-out infinite alternate; animation-delay: -11s; }
        .cn-wave-flow--echo { animation-direction: alternate-reverse; }
        @keyframes cn-mesh-flow { from { transform: translate(-30px,-40px) rotate(-3deg) scaleY(.88); } to { transform: translate(30px,40px) rotate(3deg) scaleY(1.12); } }
        @media (max-width: 1000px) { .cn-wave-art { opacity: .25; } .cn-wave-art--minimal { opacity: .10; } }
        @media (prefers-reduced-motion: reduce) { .cn-wave-flow { animation: none; } }
      `}</style>
      {(step === 0 || step === 1) && <BgSpheres />}
      {step >= 2 && (
        <div className={`cn-wave-art${dense ? " cn-wave-art--minimal" : ""}`} data-onboarding-art="monochrome-wave">
          <svg className="cn-wave-flow" opacity=".8" viewBox="0 0 1440 900" preserveAspectRatio="none" fill="none" stroke="var(--graph-line)">
            {WAVE_PATHS.map((d, i) => <path key={i} d={d} strokeWidth={i % 9 === 0 ? 1.3 : .8} strokeOpacity={.1 + (1 + Math.cos(i / 35 * Math.PI * 2)) * .16} />)}
          </svg>
          <svg className="cn-wave-flow cn-wave-flow--echo" opacity=".7" viewBox="0 0 1440 900" preserveAspectRatio="none" fill="none" stroke="var(--graph-line)">
            <g transform="translate(0 850) scale(1 -1)">
              {WAVE_PATHS.filter((_, i) => i % 2 === 0).map((d, i) => <path key={i} d={d} strokeWidth=".8" strokeOpacity={.1 + (1 + Math.sin(i / 17 * Math.PI)) * .16} />)}
            </g>
          </svg>
        </div>
      )}
    </div>
  );
}

/* Even point distribution on a sphere, computed once, plus its short edges. */
function sphere(n, seed) {
  const pts = [];
  for (let i = 0; i < n; i++) {
    const y = 1 - (i / (n - 1)) * 2;
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    const th = i * 2.399963 + seed;
    pts.push([Math.cos(th) * r, y, Math.sin(th) * r]);
  }
  const edges = [];
  for (let i = 0; i < pts.length; i++) {
    for (let j = i + 1; j < pts.length; j++) {
      const d = Math.hypot(pts[i][0] - pts[j][0], pts[i][1] - pts[j][1], pts[i][2] - pts[j][2]);
      if (d < 0.62) edges.push([i, j]);
    }
  }
  return { pts, edges };
}

/* ---------- form primitives (dark) ---------- */

const CARD = { width: "min(430px,92vw)", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "6px", boxShadow: "0 50px 110px -60px #000", padding: "26px 30px 30px" };
const LABEL = { display: "block", font: "var(--text-caption)", fontFamily: "var(--font-mono)", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text-faint)", marginBottom: "clamp(5px,0.9vh,9px)" };
const INPUT = { width: "100%", boxSizing: "border-box", font: "var(--text-body-16)", fontFamily: "var(--font-body)", color: "var(--text-primary)", background: "var(--surface-subtle)", border: "1px solid var(--border)", borderRadius: "6px", padding: "clamp(9px,1.5vh,12px) 14px", outline: "none" };

function Req() {
  return <span aria-hidden="true" style={{ color: "var(--accent)", marginLeft: "4px", verticalAlign: "-0.15em" }}>*</span>;
}

/* `hint` is an error and wins; `note` is the quiet explanation shown when there is none. */
function AppInput({ label, req, prefix, hint, note, ...p }) {
  return (
    <label style={{ display: "block", marginBottom: "clamp(9px,1.5vh,16px)" }}>
      <span style={LABEL}>{label}{req && <Req />}</span>
      <span style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        {prefix && <span style={{ font: "var(--text-body-14)", fontFamily: "var(--font-mono)", color: "var(--text-faint)", whiteSpace: "nowrap" }}>{prefix}</span>}
        <input {...p} style={INPUT} />
      </span>
      {hint
        ? <span style={{ display: "block", marginTop: "4px", font: "var(--text-caption)", color: "var(--danger)" }}>{hint}</span>
        : note && <span style={{ display: "block", marginTop: "4px", font: "var(--text-caption)", color: "var(--text-muted)" }}>{note}</span>}
    </label>
  );
}

/* ---------- the application step (pre-launch opening) ---------- */

function EaDetails({ v, set }) {
  return (
    <>
      <h1 style={{ font: "500 clamp(22px,2.2vw,30px)/1.15 var(--font-display)", letterSpacing: "-0.025em", color: "var(--text-primary)", textAlign: "center", marginBottom: "clamp(5px,0.9vh,8px)" }}>Apply for early access</h1>
      <p style={{ font: "var(--text-body-14)", color: "var(--text-muted)", textAlign: "center", marginBottom: "clamp(13px,2.4vh,26px)" }}>CodeNerve is pre-launch. We are taking a small number of teams before the product opens.</p>
      <AppInput label="Full name" req placeholder="Your name" value={v.name} onChange={e => set("name", e.target.value)} />
      <AppInput label="Work email" req type="email" placeholder="you@company.com" value={v.email} onChange={e => set("email", e.target.value)} />
      <AppInput label="Company" req placeholder="Where you work" value={v.org} onChange={e => set("org", e.target.value)} />
      <AppInput label="Company website" placeholder="example.com" value={v.site} onChange={e => set("site", e.target.value)} />
      <div>
        <span style={LABEL}>Engineers on the team</span>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: "8px" }}>
          {SIZES.map(sz => {
            const on = v.size === sz;
            return (
              <button key={sz} onClick={() => set("size", sz)}
                style={{ cursor: "pointer", font: "var(--text-caption)", fontFamily: "var(--font-mono)", letterSpacing: "0.04em", textTransform: "uppercase", padding: "clamp(7px,1.2vh,10px) 6px", borderRadius: "6px", color: on ? "var(--btn-primary-ink)" : "var(--text-secondary)", background: on ? "var(--btn-primary-bg)" : "var(--bg)", border: "1px solid " + (on ? "var(--btn-primary-bg)" : "var(--border)"), transition: "background 240ms var(--ease-out), color 240ms var(--ease-out)" }}>{sz}</button>
            );
          })}
        </div>
      </div>
    </>
  );
}


/* ---------- the closing step (pre-launch) ---------- */

function EaThanks({ v, onExit }) {
  const first = (v.name || "").trim().split(" ")[0];
  return (
    <>
      <div style={{ position: "relative", height: "clamp(74px,12vh,112px)", borderRadius: "6px", overflow: "hidden", background: "var(--surface-subtle)", border: "1px solid var(--border)", marginBottom: "clamp(13px,2.4vh,24px)" }}>
        <AppBackdrop shift={2} dense />
        <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}>
          <span style={{ display: "grid", placeItems: "center", width: "46px", height: "46px", borderRadius: "999rem", background: "var(--surface-elevated)", border: "1px solid var(--border-strong)", boxSizing: "border-box", animation: "cn-pop 520ms var(--ease-out) both" }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="var(--text-primary)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" style={{ width: "20px", height: "20px" }}><path d="M20 6 9 17l-5-5" /></svg>
          </span>
        </div>
      </div>
      <h1 style={{ font: "500 clamp(20px,2vw,26px)/1.2 var(--font-display)", letterSpacing: "-0.025em", color: "var(--text-primary)", textAlign: "center", marginBottom: "clamp(6px,1vh,10px)" }}>Thank you for applying{first ? ", " + first : ""}.</h1>
      <p style={{ font: "var(--text-body-14)", color: "var(--text-muted)", textAlign: "center", marginBottom: "clamp(6px,1vh,10px)" }}>Your application has been received. We read every one and reply from a real address, whether or not there is a place for you in this round.</p>
      <p style={{ font: "var(--text-body-14)", color: "var(--text-muted)", textAlign: "center", marginBottom: "clamp(14px,2.4vh,24px)" }}>One of us will write back within a few working days.</p>
      <div style={{ display: "grid", justifyItems: "center" }}>
        <button onClick={onExit} style={{ font: "var(--text-label)", color: "var(--text-primary)", background: "var(--surface-elevated)", border: "1px solid var(--border-strong)", borderRadius: "6px", padding: "10px 16px", cursor: "pointer" }}>Back to codenerve.ai</button>
      </div>
    </>
  );
}


/* ---------- the three steps ---------- */

function StepName({ v, set }) {
  return (
    <>
      <h1 style={{ font: "500 clamp(22px,2.2vw,30px)/1.15 var(--font-display)", letterSpacing: "-0.025em", color: "var(--text-primary)", textAlign: "center", marginBottom: "clamp(5px,0.9vh,8px)" }}>What's your name?</h1>
      <p style={{ font: "var(--text-body-14)", color: "var(--text-muted)", textAlign: "center", marginBottom: "30px" }}>Let's start by setting up your profile.</p>
      <AppInput label="Full name" req placeholder="Your name" value={v.name} onChange={e => set("name", e.target.value)} />
    </>
  );
}

const SIZES = ["Just me", "2–10", "11–50", "51–200", "201–1000", "1000+"];

/* The handle suggested for an organisation name: "Acme Payments" -> "acme-payments".
   Empty when nothing usable survives, which leaves the field for the user to fill. */
function toHandle(name) {
  return name
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+/, "")
    .slice(0, 63)
    .replace(/-+$/, "");
}

function StepOrg({ v, set, handleError }) {
  return (
    <>
      <h1 style={{ font: "500 clamp(22px,2.2vw,30px)/1.15 var(--font-display)", letterSpacing: "-0.025em", color: "var(--text-primary)", textAlign: "center", marginBottom: "clamp(5px,0.9vh,8px)" }}>Create your organisation</h1>
      <p style={{ font: "var(--text-body-14)", color: "var(--text-muted)", textAlign: "center", marginBottom: "clamp(12px,2.2vh,24px)" }}>Set up your team's workspace in CodeNerve.</p>
      <AppInput label="Organisation name" req placeholder="Acme payments" value={v.org} onChange={e => set("org", e.target.value)} />
      <AppInput label="Handle" req prefix="codenerve.ai/" placeholder="acme" value={v.handle} onChange={e => set("handle", e.target.value.replace(/\s/g, "").toLowerCase())} hint={handleError} note="Your workspace's unique ID, used in links." />
      <AppInput label="Company website" placeholder="example.com" value={v.site} onChange={e => set("site", e.target.value)} />
      <span style={LABEL}>Company size</span>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: "8px" }}>
        {SIZES.map(s => {
          const on = v.size === s;
          return (
            <button key={s} onClick={() => set("size", s)}
              style={{ cursor: "pointer", font: "var(--text-caption)", fontFamily: "var(--font-mono)", letterSpacing: "0.04em", textTransform: "uppercase", padding: "11px 6px", borderRadius: "6px", color: on ? "var(--btn-primary-ink)" : "var(--text-secondary)", background: on ? "var(--btn-primary-bg)" : "var(--bg)", border: "1px solid " + (on ? "var(--btn-primary-bg)" : "var(--border)"), transition: "background 240ms var(--ease-out), color 240ms var(--ease-out), border-color 240ms var(--ease-out)" }}>{s}</button>
          );
        })}
      </div>
    </>
  );
}

const CHALLENGES = ["Slow PR reviews", "Previous AI tools didn't work well", "Too many bugs", "Improve code quality", "Team scaling", "Other"];
const SOURCES = ["Google", "A colleague", "GitHub", "Social", "A conference", "Somewhere else"];

function StepWhy({ v, set }) {
  const toggle = c => set("why", v.why.includes(c) ? v.why.filter(x => x !== c) : v.why.concat(c));
  return (
    <>
      <h1 style={{ font: "500 clamp(22px,2.2vw,30px)/1.15 var(--font-display)", letterSpacing: "-0.025em", color: "var(--text-primary)", textAlign: "center", marginBottom: "clamp(5px,0.9vh,8px)" }}>What brought you to CodeNerve?</h1>
      <p style={{ font: "var(--text-body-14)", color: "var(--text-muted)", textAlign: "center", marginBottom: "clamp(12px,2.2vh,24px)" }}>Help us understand your needs so we can serve you better.</p>
      <span style={LABEL}>Describe the challenges you're facing</span>
      <div className="cn-challenges" style={{ display: "grid", gap: "clamp(4px,0.7vh,6px)", marginBottom: "clamp(11px,2vh,20px)" }}>
        {CHALLENGES.map((c, i) => {
          const on = v.why.includes(c);
          return (
            <button key={c} onClick={() => toggle(c)}
              style={{ display: "grid", gridTemplateColumns: "20px 1fr", gap: "13px", alignItems: "center", textAlign: "left", cursor: "pointer", padding: "clamp(7px,1.3vh,11px) 14px", borderRadius: "6px", background: on ? "var(--surface-elevated)" : "var(--surface-subtle)", border: "1px solid " + (on ? "var(--border-stronger)" : "var(--border)"), transition: "background 240ms var(--ease-out), border-color 240ms var(--ease-out)", animation: `cn-fade-up 420ms var(--ease-out) ${i * 50}ms both` }}>
              <span style={{ display: "grid", placeItems: "center", width: "18px", height: "18px", borderRadius: "5px", background: on ? "var(--control-on-bg)" : "transparent", border: "1px solid " + (on ? "var(--control-on-border)" : "var(--border-strong)"), transition: "background 240ms var(--ease-out)" }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="var(--control-on-ink)" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" style={{ width: "11px", height: "11px", opacity: on ? 1 : 0, transform: on ? "scale(1)" : "scale(0.6)", transition: "opacity 220ms var(--ease-out), transform 220ms var(--ease-out)" }}><path d="M20 6 9 17l-5-5" /></svg>
              </span>
              <span style={{ font: "var(--text-body-14)", color: on ? "var(--text-primary)" : "var(--text-secondary)" }}>{c}</span>
            </button>
          );
        })}
      </div>
      <span style={LABEL}>How did you hear about us?</span>
      <select value={v.source} onChange={e => set("source", e.target.value)} style={{ ...INPUT, appearance: "none", cursor: "pointer" }}>
        {SOURCES.map(s => <option key={s} value={s} style={{ background: "var(--surface-subtle)" }}>{s}</option>)}
      </select>
    </>
  );
}


/* ---------- batch 2: repository graph ---------- */

const CHIP_TONES = [
  { bg: "var(--surface-elevated)", fg: "var(--text-primary)" }, { bg: "var(--border)", fg: "var(--text-primary)" },
  { bg: "var(--border-strong)", fg: "var(--text-primary)" }, { bg: "var(--surface)", fg: "var(--text-secondary)" },
  { bg: "var(--border-stronger)", fg: "var(--text-primary)" }
];
const GLYPHS = ["{ }", "</>", "TS", "md", "js", "py", "rs", "go"];
const GRAPH = sphere(64, 0.9);
/* Sparse chips, and no name a callout already claims. Each candidate node is bound to
   its own filename up front, keyed by node index, so a name can never appear twice
   however many nodes the depth filter drops. */
const CHIP_AT = GRAPH.pts.map((_, i) => i).filter(i => i % 7 === 2);
const CHIP_FILES = ["api.rs", "auth.py", "db.go", "queue.ts", "chart.tsx", "main.py", "router.ts", "cache.rs", "types.d.ts", "worker.go", "schema.sql", "hooks.ts"];
const CHIP_NAME = CHIP_AT.reduce((m, i, k) => { m[i] = CHIP_FILES[k % CHIP_FILES.length]; return m; }, {});

/* One recurring figure: a node sphere with file chips embedded, turning slowly.
   labelled=true adds the figure header and leader-lined callouts. */
function GraphFigureHead({ repo }) {
  const short = (repo || "").split("/").pop();
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "9px", flexWrap: "wrap", justifyContent: "center", marginBottom: "14px", animation: "cn-fade-up 620ms var(--ease-out) both" }}>
      <span style={{ font: "var(--text-caption)", fontFamily: "var(--font-mono)", letterSpacing: "0.08em", color: "var(--text-faint)" }}>FIG.1 CODEGRAPH FOR</span>
      <span style={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", font: "var(--text-caption)", fontFamily: "var(--font-mono)", letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--btn-primary-ink)", background: "var(--btn-primary-bg)", padding: "3px 8px", borderRadius: "4px" }}>{short}</span>
    </div>
  );
}

function CodeGraphSphere({ size, labelled, repo }) {
  const px = p => [50 + p[0] * 44, 50 - p[1] * 44];
  const CALLOUTS = [
    { t: "index.ts", x: "-4%", y: "18%", ax: 12, ay: 34 },
    { t: "README.md", x: "76%", y: "14%", ax: 86, ay: 28 },
    { t: "package.json", x: "-2%", y: "80%", ax: 20, ay: 72 },
    { t: "utils.ts", x: "78%", y: "74%", ax: 82, ay: 66 }
  ];
  /* Depth decides everything: back half recedes to a hairline, front half is drawn
     crisp and bright, so the figure reads as a solid object instead of a haze. */
  const back = GRAPH.edges.filter(([x, y]) => (GRAPH.pts[x][2] + GRAPH.pts[y][2]) / 2 <= 0);
  const front = GRAPH.edges.filter(([x, y]) => (GRAPH.pts[x][2] + GRAPH.pts[y][2]) / 2 > 0);
  const edge = (pair, i, isFront) => {
    const [x, y] = pair, u = px(GRAPH.pts[x]), w = px(GRAPH.pts[y]);
    const d = (GRAPH.pts[x][2] + GRAPH.pts[y][2]) / 2;
    return <line key={(isFront ? "f" : "b") + i} x1={u[0]} y1={u[1]} x2={w[0]} y2={w[1]}
      stroke={isFront ? "var(--graph-node)" : "var(--graph-line)"} strokeWidth={isFront ? 0.22 : 0.13} opacity={isFront ? 0.28 + d * 0.5 : 0.16 + (d + 1) * 0.08} />;
  };
  return (
    <div style={{ position: "relative", width: size, maxWidth: "100%", aspectRatio: "1", margin: "0 auto" }}>
      {/* core glow, gives the sphere a lit centre rather than a flat wireframe */}
      <div aria-hidden="true" style={{ position: "absolute", left: "50%", top: "50%", width: "78%", height: "78%", transform: "translate(-50%,-50%)", borderRadius: "999rem", background: "radial-gradient(circle, #ffffff0f 0%, #ffffff07 46%, transparent 72%)" }}></div>

      <svg viewBox="0 0 100 100" aria-hidden="true" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
        <g style={{ transformOrigin: "50% 50%", animation: "cn-turn 62s linear infinite" }}>
          {back.map((e, i) => edge(e, i, false))}
          {GRAPH.pts.filter(q => q[2] <= 0).map((q, i) => {
            const c = px(q);
            return <circle key={"bp" + i} cx={c[0]} cy={c[1]} r="0.5" fill="var(--graph-line)" opacity={0.18 + (q[2] + 1) * 0.12} />;
          })}
          {front.map((e, i) => edge(e, i, true))}
          {GRAPH.pts.filter(q => q[2] > 0).map((q, i) => {
            const c = px(q), r = 0.55 + q[2] * 0.7;
            return (
              <g key={"fp" + i}>
                <circle cx={c[0]} cy={c[1]} r={r * 2.6} fill="var(--graph-halo)" opacity={0.06 + q[2] * 0.1} />
                <circle cx={c[0]} cy={c[1]} r={r} fill="var(--text-primary)" opacity={0.5 + q[2] * 0.5} />
              </g>
            );
          })}
        </g>
        {/* one still equator: a fixed reference the turning mesh reads against */}
        <ellipse cx="50" cy="50" rx="44" ry="10" fill="none" stroke="var(--border-stronger)" strokeWidth="0.18" opacity="0.3" />
        <circle cx="50" cy="50" r="44" fill="none" stroke="var(--border-stronger)" strokeWidth="0.16" opacity="0.18" />
      </svg>

      <div aria-hidden="true" style={{ position: "absolute", inset: 0, animation: "cn-turn 62s linear infinite", transformOrigin: "50% 50%" }}>
        {CHIP_AT.map((i, k) => {
          const q = GRAPH.pts[i], c = px(q), t = CHIP_TONES[k % CHIP_TONES.length];
          if (q[2] < 0.3) return null;             /* front cap only, no smear behind the mesh */
          if (Math.hypot(c[0] - 50, c[1] - 50) > 25) return null;  /* inner disc only, the rim belongs to the callouts */
          const near = (q[2] + 1) / 2;
          return (
            <span key={i} style={{ position: "absolute", left: c[0] + "%", top: c[1] + "%", transform: "translate(-50%,-50%)", animation: "cn-turn 62s linear infinite reverse", transformOrigin: "50% 50%" }}>
              <span style={{ display: "block", transform: `scale(${0.8 + near * 0.45})`, padding: "3px 6px", borderRadius: "5px", background: t.bg, color: t.fg, font: "9px/1 var(--font-mono)", whiteSpace: "nowrap", boxShadow: "0 4px 12px -4px #000a", border: "1px solid var(--border-strong)", opacity: 0.72 + near * 0.28, animation: `cn-fade-up 700ms var(--ease-out) ${k * 30}ms both` }}>{CHIP_NAME[i]}</span>
            </span>
          );
        })}
      </div>

      {labelled && (
        <>
          <svg viewBox="0 0 100 100" aria-hidden="true" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", overflow: "visible" }}>
            {CALLOUTS.map((c, i) => (
              <line key={c.t} x1={c.ax} y1={c.ay} x2={50 + (c.ax - 50) * 0.16} y2={50 + (c.ay - 50) * 0.16}
                stroke="var(--text-muted)" strokeWidth="0.22" style={{ opacity: 0, animation: `cn-fade-up 520ms var(--ease-out) ${700 + i * 140}ms forwards` }} />
            ))}
          </svg>
          {CALLOUTS.map((c, i) => (
            <span key={c.t} style={{ position: "absolute", left: c.x, top: c.y, font: "var(--text-caption)", fontFamily: "var(--font-mono)", color: "var(--text-primary)", background: "var(--surface)", border: "1px solid var(--border)", padding: "4px 8px", borderRadius: "5px", whiteSpace: "nowrap", animation: `cn-fade-up 520ms var(--ease-out) ${700 + i * 140}ms both` }}>{c.t}</span>
          ))}
        </>
      )}
    </div>
  );
}

/* What the API reports for a repository, in the user's words. `not_indexed` is one the
   dispatcher has not reached yet, which reads as queued rather than as a failure. */
const INDEX_LABEL = {
  not_indexed: ["Queued", "var(--text-faint)"],
  indexing: ["Indexing", "var(--text-secondary)"],
  indexed: ["Ready", "var(--success)"],
  failed: ["Failed", "var(--danger)"]
};

/* A disabled repository is neither indexed nor reviewed, and the indexing counts leave it
   out. Showing its last index status instead read as a failure the totals did not add up
   to. */
function indexLabel(repo) {
  if (!repo.enabled) return ["Disabled", "var(--text-faint)"];
  return INDEX_LABEL[repo.indexStatus] || ["Unknown", "var(--text-faint)"];
}

/* `indexError` is a safe label from the worker -- a known code or an exception class
   name, never a provider message -- so it can be put in words without leaking anything.
   Each message says whose problem it is. A provider outage or exhausted credits arrive as
   `index_incomplete`: nothing the customer did and nothing they can fix, so it says so
   rather than naming the provider. Lost GitHub access is the one they can act on. */
const INDEX_ERROR = {
  index_incomplete: "Indexing couldn't finish because of a temporary problem on our side. Nothing is wrong with your repository.",
  installation_unavailable: "CodeNerve no longer has access to this repository on GitHub. Reconnect it in Settings → Code providers, then retry."
};

function indexErrorText(repo) {
  if (!repo.enabled || repo.indexStatus !== "failed") return null;
  return INDEX_ERROR[repo.indexError] || "Something went wrong on our side while indexing. Try again in a few minutes; if it keeps failing, contact support.";
}

/* Retry for one failed repository. The caller re-reads the list afterwards, so the row
   moves to Queued by itself; this only owns the click's busy and error state. */
function RetryButton({ repo, onRetry }) {
  const [busy, setBusy] = React.useState(false);
  const [failure, setFailure] = React.useState("");
  const retry = async () => {
    if (busy) return;
    setBusy(true);
    setFailure("");
    try {
      await onRetry(repo.id);
    } catch (error) {
      setFailure(error?.message || "Could not retry. Try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "10px", flexWrap: "wrap", marginTop: "6px" }}>
      <button type="button" disabled={busy} aria-busy={busy} onClick={retry} style={{ font: "var(--text-caption)", color: "var(--text-primary)", background: "var(--surface-elevated)", border: "1px solid var(--border-strong)", borderRadius: "6px", padding: "4px 10px", cursor: busy ? "default" : "pointer" }}>{busy ? "Retrying..." : "Retry"}</button>
      {failure && <span role="alert" style={{ font: "var(--text-caption)", color: "var(--danger)" }}>{failure}</span>}
    </span>
  );
}

/* The share of the ring each worker step owns, as [start, end, seconds to settle]. The
   worker says which step it is on, never how far through, so within a step the ring eases
   toward the step's end without reaching it, and only the next step moves it past. That
   keeps the ring from claiming work the worker has not done. A retry re-runs only the
   missing work, so it sits at the end of the ring rather than back at the start. */
const INDEX_STEP_BAND = {
  checkout: [0.04, 0.15, 8],
  graph: [0.15, 0.4, 15],
  chunks: [0.4, 0.78, 60],
  embed: [0.78, 0.94, 30],
  retry: [0.78, 0.96, 60]
};
const RING_TICK_MS = 600;
const RING_HOLD_MS = 1400;

/* Each repository's ring, kept outside the component: every wizard step mounts its own
   rows, so a ring held in component state restarted from empty on each Back or Next.
   Keyed by repository id, holding { fill, step, entered, finishedAt }. */
const ringProgress = new Map();

function ringFinishedLongAgo(saved) {
  return Boolean(saved?.finishedAt) && Date.now() - saved.finishedAt >= RING_HOLD_MS;
}

/* A Play Store style download ring for one repository: shown while it indexes, filled and
   turned green when it finishes, then gone. Failed or queued repositories get none. */
function IndexRing({ repo, size = 14 }) {
  const active = repo.enabled && repo.indexStatus === "indexing";
  const step = repo.indexStep || "";
  const [fill, setFill] = React.useState(() => ringProgress.get(repo.id)?.fill ?? 0);
  const [gone, setGone] = React.useState(() => ringFinishedLongAgo(ringProgress.get(repo.id)));

  React.useEffect(() => {
    if (!active) return undefined;
    let saved = ringProgress.get(repo.id);
    /* A new run starts from empty, even if an earlier one in this session finished. */
    if (!saved || saved.finishedAt) {
      saved = { fill: 0, step: null, entered: 0, finishedAt: 0 };
      ringProgress.set(repo.id, saved);
      setGone(false);
    }
    /* The step's clock starts when the step is first seen, not when this row mounts, so
       coming back to a step resumes the ring rather than replaying it. */
    if (saved.step !== step) {
      saved.step = step;
      saved.entered = Date.now();
    }
    const [start, end, settle] = INDEX_STEP_BAND[step] || [0, 0.04, 6];
    /* Never backwards: the worker restarts at checkout for a retry, and a ring that
       emptied itself would read as lost work. */
    const tick = () => {
      const elapsed = (Date.now() - saved.entered) / 1000;
      saved.fill = Math.max(saved.fill, start + (end - start) * (1 - Math.exp(-elapsed / settle)));
      setFill(saved.fill);
    };
    tick();
    const timer = setInterval(tick, RING_TICK_MS);
    return () => clearInterval(timer);
  }, [active, step, repo.id]);

  const finishing = Boolean(ringProgress.get(repo.id)) && repo.enabled && repo.indexStatus === "indexed";
  React.useEffect(() => {
    if (!finishing) return undefined;
    const saved = ringProgress.get(repo.id);
    if (!saved.finishedAt) saved.finishedAt = Date.now();
    saved.fill = 1;
    setFill(1);
    const timer = setTimeout(() => setGone(true), Math.max(0, RING_HOLD_MS - (Date.now() - saved.finishedAt)));
    return () => clearTimeout(timer);
  }, [finishing, repo.id]);

  if (!active && !(finishing && !gone)) return null;
  const radius = 6;
  const circumference = 2 * Math.PI * radius;
  return (
    <svg aria-hidden="true" data-index-ring="" viewBox="0 0 16 16" style={{ width: size + "px", height: size + "px", flex: "0 0 auto", transform: "rotate(-90deg)" }}>
      <circle cx="8" cy="8" r={radius} fill="none" stroke="var(--border-strong)" strokeWidth="2" />
      <circle cx="8" cy="8" r={radius} fill="none" stroke={finishing ? "var(--success)" : "var(--text-primary)"} strokeWidth="2" strokeLinecap="round"
        strokeDasharray={circumference} strokeDashoffset={circumference * (1 - fill)}
        style={{ transition: `stroke-dashoffset ${RING_TICK_MS}ms linear, stroke 320ms var(--ease-out)` }} />
    </svg>
  );
}

const WIDE_TITLE = { font: "500 clamp(21px,2.1vw,28px)/1.15 var(--font-display)", letterSpacing: "-0.025em", color: "var(--text-primary)", marginBottom: "10px" };
const PANEL = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "6px", overflow: "hidden" };

/* The kit ships the real provider marks; use them rather than a drawn glyph. */
function GitMark({ provider = "github", size = 14, onDark = true }) {
  const invert = provider === "github" && onDark;
  return <img src={githubMark} alt="" aria-hidden="true" style={{ width: size + "px", height: size + "px", objectFit: "contain", flex: "0 0 auto", filter: invert ? "brightness(0) invert(1)" : "none" }} />;
}

/* A4, connect repositories. Installing hands the browser to github.com, which owns the
   repository picker -- we never see a token or a password, and the App can read only what
   the user ticks there. */
function StepRepos({ v, github, onConnect, busy }) {
  const count = github.counts?.submitted ?? 0;
  const steps = github.connected
    ? [["GitHub App installed", "reading through the installation"],
       ["Repositories chosen", count + (count === 1 ? " repository" : " repositories")]]
    : [["Install the GitHub App", "on github.com, in a moment"],
       ["Choose repositories", "you pick them; we see only those"]];
  return (
    <div style={{ width: "min(1180px,100%)", display: "grid", gridTemplateColumns: "minmax(0,0.92fr) minmax(0,1.08fr)", gap: "clamp(24px,4vw,64px)", alignItems: "center" }}>
      <div>
        <h1 style={WIDE_TITLE}>{github.connected ? "Your repositories are connected." : "Connect your repositories."}</h1>
        <p style={{ font: "var(--text-body-14)", color: "var(--text-muted)", maxWidth: "46ch", marginBottom: "24px" }}>CodeNerve reads them through the GitHub App. You choose which repositories on GitHub, and nothing is written back until you say so.</p>
        <div style={{ ...PANEL, padding: "22px 24px" }}>
          {steps.map((st, i) => (
            <div key={st[0]} style={{ position: "relative", display: "grid", gridTemplateColumns: "26px 1fr", gap: "14px", alignItems: "start", paddingBottom: i < 2 ? "26px" : 0 }}>
              {i < 2 && <span aria-hidden="true" style={{ position: "absolute", left: "12px", top: "26px", bottom: "8px", width: "2px", background: "var(--border)" }}></span>}
              {i < 2 && <span aria-hidden="true" style={{ position: "absolute", left: "12px", top: "26px", width: "2px", height: "0", background: "var(--border-stronger)", animation: `cn-grow-y 520ms var(--ease-out) ${520 + i * 620}ms forwards` }}></span>}
              <span style={{ display: "grid", placeItems: "center", width: "24px", height: "24px", borderRadius: "999rem", background: "var(--bg)", border: "2px solid var(--text-primary)", animation: `cn-pop 460ms var(--ease-out) ${i * 620}ms both` }}>
                <span style={{ width: "8px", height: "8px", borderRadius: "999rem", background: "var(--border-stronger)" }}></span>
              </span>
              <span style={{ animation: `cn-fade-up 520ms var(--ease-out) ${i * 620 + 80}ms both` }}>
                <span style={{ display: "block", font: "var(--text-body-14)", color: "var(--text-primary)" }}>{i + 1}. {st[0]}</span>
                <span style={{ font: "var(--text-caption)", fontFamily: "var(--font-mono)", color: "var(--text-faint)" }}>{st[1]}</span>
              </span>
            </div>
          ))}
        </div>
        <button disabled={busy} aria-busy={busy} onClick={onConnect} style={{ marginTop: "22px", display: "inline-flex", alignItems: "center", gap: "10px", font: "var(--text-label)", color: "var(--btn-primary-ink)", background: "var(--btn-primary-bg)", border: "none", borderRadius: "var(--radius-btn)", padding: "12px 18px", cursor: "pointer" }}>
          <GitMark onDark={false} />
          {busy ? "Connecting..." : github.connected ? "Change repositories on GitHub" : "Continue with GitHub"}
        </button>
      </div>
      <div>
        <CodeGraphSphere size="min(58vh,560px)" />
        <p style={{ font: "var(--text-caption)", fontFamily: "var(--font-mono)", letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--text-faint)", textAlign: "center", marginTop: "22px", animation: "cn-fade-up 620ms var(--ease-out) 1.6s both" }}>
          {github.connected ? "Repositories connected. CodeNerve is constructing a graph of your repos" : "Awaiting repository access"}
        </p>
      </div>
    </div>
  );
}

/* A5, indexing */
function StepIndexing({ v, github }) {
  const repos = github.repositories;
  const counts = github.counts;
  const figure = (repos[0]?.name || v.org || "your repository").split("/").pop();
  /* Failures are final -- nothing retries them -- so "in progress" is only true while a
     repository is still queued or indexing. */
  const pending = (counts?.indexing ?? 0) + (counts?.notIndexed ?? 0);
  const failed = counts?.failed ?? 0;
  const disabled = repos.filter((r) => !r.enabled).length;
  const finishedWithErrors = !counts?.complete && !pending && failed > 0;
  const headline = counts?.complete
    ? "Indexing complete"
    : finishedWithErrors ? "Indexing finished with errors"
    : repos.length ? "Indexing in progress" : "Nothing connected yet";
  const tally = `${counts?.indexed ?? 0} of ${counts?.submitted ?? repos.length - disabled} indexed`
    + (failed ? `, ${failed} failed` : "")
    + (disabled ? `, ${disabled} disabled` : "");
  const followUp = finishedWithErrors
    ? "Failed repositories are not reviewed until they index."
    : pending ? "You can continue while this finishes." : "";
  return (
    <div style={{ width: "min(1180px,100%)", display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: "clamp(24px,4vw,56px)", alignItems: "start" }}>
      <div>
        <h1 style={WIDE_TITLE}>CodeNerve is indexing your repos.</h1>
        <p style={{ font: "var(--text-body-14)", color: "var(--text-muted)", maxWidth: "52ch", marginBottom: "22px" }}>Before reviewing your code, CodeNerve builds a full node graph of every repository. This step takes a little time, but it is what makes each review context-aware rather than diff-deep.</p>
        <div style={{ ...PANEL, marginBottom: "16px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 120px", gap: "16px", padding: "13px 18px", borderBottom: "1px solid var(--border)", font: "var(--text-caption)", fontFamily: "var(--font-mono)", letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-faint)" }}>
            <span>Repositories</span><span>Status</span>
          </div>
          <div style={{ maxHeight: "min(38vh,300px)", overflowY: "auto" }}>
            {repos.length === 0 && (
              <div style={{ padding: "26px 18px", textAlign: "center", font: "var(--text-body-14)", color: "var(--text-muted)" }}>
                No repositories yet. Go back a step and connect GitHub.
              </div>
            )}
            {repos.map((r, i) => {
              const [label, colour] = indexLabel(r);
              const reason = indexErrorText(r);
              return (
                <div key={r.id} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 120px", gap: "16px", alignItems: "center", padding: "12px 18px", borderTop: i ? "1px solid var(--surface-elevated)" : "none", animation: `cn-fade-up 420ms var(--ease-out) ${Math.min(i, 12) * 70}ms both` }}>
                  <span style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                    <GitMark />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: "block", font: "var(--text-body-14)", color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</span>
                      {reason && <span style={{ display: "block", font: "var(--text-caption)", color: "var(--text-muted)" }}>{reason}</span>}
                      {reason && github.onRetry && <RetryButton repo={r} onRetry={github.onRetry} />}
                    </span>
                  </span>
                  {/* indexStep names the stage the worker reached; the ring fills by
                      stage rather than by a percentage nobody can compute before the
                      work is done. */}
                  <span title={r.indexError || r.indexStep || ""} style={{ display: "inline-flex", alignItems: "center", gap: "8px", font: "var(--text-caption)", fontFamily: "var(--font-mono)", color: colour }}><IndexRing repo={r} />{label}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "14px", ...PANEL, background: "var(--surface)", padding: "13px 17px", animation: "cn-fade-up 620ms var(--ease-out) 1.9s both" }}>
          <span style={{ width: "7px", height: "7px", borderRadius: "999rem", background: counts?.complete ? "var(--success)" : finishedWithErrors ? "var(--danger)" : repos.length ? "var(--warning)" : "var(--border-stronger)", flex: "0 0 auto" }}></span>
          <span>
            <span style={{ display: "block", font: "var(--text-label)", color: "var(--text-primary)" }}>{headline}</span>
            <span style={{ font: "var(--text-caption)", fontFamily: "var(--font-mono)", color: "var(--text-muted)" }}>
              {repos.length
                ? `${tally}.${followUp ? ` ${followUp}` : ""}`
                : "Connect GitHub to submit repositories for indexing."}
            </span>
          </span>
        </div>
      </div>
      <div>
        <GraphFigureHead repo={figure} />
        <CodeGraphSphere size="min(52vh,500px)" labelled repo={figure} />
      </div>
    </div>
  );
}

/* A6. AI rules files found. Background: floating rule chips + crosshairs. */
const FLOAT_CHIPS = [
  { t: "STYLE GUIDE", x: "5%", y: "6%", c: "var(--text-faint)" }, { t: "CURSOR.RULES", x: "76%", y: "8%", c: "var(--text-faint)" },
  { t: "RULE", x: "3%", y: "28%", c: "var(--text-faint)" }, { t: "RULE", x: "84%", y: "33%", c: "var(--text-faint)" },
  { t: "CLAUDE.MD", x: "5%", y: "58%", c: "var(--text-faint)" }, { t: "AGENT.MD", x: "82%", y: "62%", c: "var(--text-faint)" },
  { t: "COPILOT", x: "6%", y: "88%", c: "var(--text-faint)" }, { t: "AGENTS.MD", x: "80%", y: "90%", c: "var(--text-faint)" }
];
const CROSSES = [["11%", "5%"], ["19%", "24%"], ["15%", "52%"], ["7%", "66%"], ["17%", "88%"], ["77%", "5%"], ["93%", "17%"], ["87%", "35%"], ["83%", "50%"], ["91%", "68%"], ["79%", "92%"]];

function RulesBackdrop() {
  return (
    <div aria-hidden="true" style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
      {CROSSES.map(([x, y], i) => (
        <span key={i} style={{ position: "absolute", left: x, top: y, width: "14px", height: "14px", opacity: 0.5, animation: `cn-wave-drift ${16 + i}s ease-in-out ${-i}s infinite alternate` }}>
          <span style={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: "1px", background: "var(--text-disabled)" }}></span>
          <span style={{ position: "absolute", top: "50%", left: 0, right: 0, height: "1px", background: "var(--text-disabled)" }}></span>
        </span>
      ))}
      {FLOAT_CHIPS.map((c, i) => (
        <span key={c.t + i} style={{ position: "absolute", left: c.x, top: c.y, display: "inline-flex", alignItems: "center", gap: "6px", font: "10px/1 var(--font-mono)", letterSpacing: "0.06em", color: "var(--text-primary)", background: "var(--surface-elevated)", border: "1px solid " + c.c + "80", padding: "5px 9px", borderRadius: "5px", whiteSpace: "nowrap", animation: `cn-wave-drift ${13 + i * 1.4}s ease-in-out ${-i * 1.7}s infinite alternate` }}>
          <span style={{ width: "6px", height: "6px", borderRadius: "2px", background: c.c }}></span>{c.t}
        </span>
      ))}
    </div>
  );
}

/* The prototype listed rule files it had "found" in eight invented repositories, counts
   and all. Nothing reads rule files yet, and Memory is off until it is shown not to lower
   review quality, so the screen only shows how far each repository's indexing has got. */
function StepRules({ v, github }) {
  const repos = github.repositories;
  const indexed = github.counts?.indexed ?? 0;
  const submitted = github.counts?.submitted ?? repos.filter((r) => r.enabled).length;
  return (
    <>
      <h1 style={{ font: "500 clamp(20px,2vw,26px)/1.2 var(--font-display)", letterSpacing: "-0.025em", color: "var(--text-primary)", textAlign: "center", marginBottom: "clamp(5px,0.9vh,8px)" }}>CodeNerve is reading your AI rules files.</h1>
      <p style={{ font: "var(--text-body-14)", color: "var(--text-muted)", textAlign: "center", marginBottom: "clamp(12px,2.2vh,24px)" }}>{repos.length ? `${indexed} of ${submitted} repositories read so far.` : "Nothing connected yet."}</p>
      <div style={PANEL}>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1.15fr)", gap: "12px", padding: "11px 16px", borderBottom: "1px solid var(--border)", font: "var(--text-caption)", fontFamily: "var(--font-mono)", letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-faint)" }}>
          <span>Repositories</span><span>Status</span>
        </div>
        <div style={{ maxHeight: "min(34vh,270px)", overflowY: "auto" }}>
          {repos.length === 0 && (
            <div style={{ padding: "24px 16px", textAlign: "center", font: "var(--text-body-14)", color: "var(--text-muted)" }}>
              Connect GitHub to have your rules files read.
            </div>
          )}
          {repos.map((row, i) => {
            const [label, colour] = indexLabel(row);
            return (
              <div key={row.id} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1.15fr)", gap: "12px", alignItems: "center", padding: "11px 16px", borderTop: i ? "1px solid var(--surface-elevated)" : "none", animation: `cn-fade-up 420ms var(--ease-out) ${Math.min(i, 12) * 90}ms both` }}>
                <span style={{ display: "flex", alignItems: "center", gap: "9px", minWidth: 0 }}>
                  <GitMark />
                  <span style={{ font: "var(--text-body-14)", color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.name}</span>
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "8px", font: "var(--text-caption)", fontFamily: "var(--font-mono)", color: colour }}><IndexRing repo={row} />{label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}


/* ---------- batch 3: agent configuration (A7–A10) ---------- */

const TABS = ["PR analysis", "Comment scope", "Commit behaviour", "Developer tools"];

const SUMMARY_ITEMS = [
  { k: "summary", h: "PR summary", d: "Include a text summary of the changes", i: "file-text" },
  { k: "sequence", h: "Sequence diagrams", d: "Generate a sequence diagram of the changes", i: "git-branch" },
  { k: "issues", h: "Issue table", d: "Show a table of the important files changed", i: "table" },
  { k: "score", h: "Confidence score", d: "Include a confidence score out of five for the PR", i: "bar-chart-3" },
  { k: "outside", h: "Comments outside diff", d: "Allow comments on lines not in the diff", i: "message-circle" }
];

function TabStrip({ tab }) {
  return (
    <ol aria-label="Review settings" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", flexWrap: "wrap", listStyle: "none", padding: 0, maxWidth: "1180px", margin: "0 auto 26px" }}>
      {TABS.map((t, i) => {
        const on = i === tab, done = i < tab;
        return (
          <li key={t} aria-current={on ? "step" : undefined} style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
            {i > 0 && <span aria-hidden="true" style={{ width: "18px", height: "1px", background: done || on ? "var(--border-stronger)" : "var(--border)" }}></span>}
            <span style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "6px 12px 6px 7px", borderRadius: "999rem", border: "1px solid " + (on ? "var(--border-stronger)" : "var(--border)"), background: on ? "var(--surface-elevated)" : "transparent", font: "var(--text-caption)", color: on ? "var(--text-primary)" : done ? "var(--text-secondary)" : "var(--text-faint)", transition: "background 320ms var(--ease-out), color 320ms var(--ease-out), border-color 320ms var(--ease-out)" }}>
              <span aria-hidden="true" style={{ display: "grid", placeItems: "center", width: "18px", height: "18px", borderRadius: "999rem", font: "500 10px/1 var(--font-mono)", background: on ? "var(--text-primary)" : done ? "var(--surface-elevated)" : "transparent", color: on ? "var(--bg)" : "var(--text-muted)", border: on ? "none" : "1px solid var(--border-strong)" }}>
                {done ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: "10px", height: "10px" }}><path d="M20 6 9 17l-5-5" /></svg> : i + 1}
              </span>
              {t}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

const ROW = { display: "grid", gap: "4px", padding: "18px 20px", borderTop: "1px solid var(--surface-elevated)" };

function Toggle({ on, onClick }) {
  return (
    <button type="button" role="switch" aria-checked={on} onClick={onClick} style={{ position: "relative", width: "42px", height: "24px", flex: "0 0 auto", borderRadius: "999rem", cursor: "pointer", border: "1px solid " + (on ? "var(--control-on-border)" : "var(--border-strong)"), background: on ? "var(--control-on-bg)" : "var(--surface-subtle)", transition: "background 260ms var(--ease-out), border-color 260ms var(--ease-out)" }}>
      <span style={{ position: "absolute", top: "2px", left: on ? "20px" : "2px", width: "18px", height: "18px", borderRadius: "999rem", background: on ? "var(--control-on-ink)" : "var(--text-faint)", transition: "left 260ms var(--ease-out), background 260ms var(--ease-out)" }}></span>
    </button>
  );
}

/* preview furniture */
const PV = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "6px", overflow: "hidden" };
function PvHead({ t, right }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "11px 14px", background: "var(--surface-elevated)", borderBottom: "1px solid var(--surface-elevated)" }}>
      <span style={{ font: "var(--text-caption)", fontFamily: "var(--font-mono)", color: "var(--text-primary)" }}>{t}</span>
      {right && <span style={{ marginLeft: "auto" }}>{right}</span>}
    </div>
  );
}
function Bar({ w, tone }) {
  return <span style={{ display: "block", height: "6px", width: w, borderRadius: "999rem", background: tone || "var(--border-strong)" }}></span>;
}

/* A7 preview: the review comment, assembled from whatever is switched on */
function SummaryPreview({ c }) {
  const sections = SUMMARY_ITEMS.filter(x => c[x.k].on);
  return (
    <div className="cn-summary-preview" style={{ ...PV, animation: "cn-fade-up 520ms var(--ease-out) both" }}>
      <PvHead t="codenerve" right={<span style={{ font: "10px/1 var(--font-mono)", color: "var(--text-faint)" }}>review comment</span>} />
      <div style={{ padding: "12px", display: "grid", gap: "10px" }}>
        {sections.length === 0 && <span style={{ font: "var(--text-caption)", color: "var(--text-faint)", padding: "18px 6px" }}>Everything is off. CodeNerve would post nothing.</span>}
        {sections.map((x, i) => {
          const cfg = c[x.k], open = !cfg.collapsible || cfg.defaultOpen;
          return (
            <div key={x.k} style={{ ...PV, background: "var(--surface-subtle)", animation: "cn-fade-up 420ms var(--ease-out) " + (i * 70) + "ms both" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "9px", padding: "9px 12px", background: "var(--border)" }}>
                {cfg.collapsible && <span style={{ font: "10px", color: "var(--text-secondary)", transform: open ? "rotate(90deg)" : "none", transition: "transform 260ms var(--ease-out)" }}>▸</span>}
                <span style={{ font: "11px/1 var(--font-mono)", color: "var(--text-primary)" }}>{x.h}</span>
              </div>
              {open && (
                <div style={{ padding: "12px", display: "grid", gap: "7px" }}>
                  {x.k === "issues" ? (
                    <div style={{ display: "grid", gap: "6px" }}>
                      {[["File", "Score", "Overview"], ["src/app.tsx", "3/5", "Major structure"], ["src/utils.ts", "5/5", "Minor cleanup"]].map((r, k) => (
                        <div key={r[0]} style={{ display: "grid", gridTemplateColumns: "1.2fr .5fr 1fr", gap: "8px", font: "10px/1.4 var(--font-mono)", color: k ? "var(--text-secondary)" : "var(--text-faint)" }}>
                          <span>{r[0]}</span><span>{r[1]}</span><span>{r[2]}</span>
                        </div>
                      ))}
                    </div>
                  ) : x.k === "score" ? (
                    <span style={{ font: "12px/1 var(--font-mono)", color: "var(--text-primary)" }}>4/5, safe to merge with one blocking note</span>
                  ) : x.k === "sequence" ? (
                    <div style={{ display: "grid", gap: "8px", paddingTop: "4px" }}>
                      <span style={{ display: "flex", gap: "8px" }}><Bar w="26%" tone="var(--text-faint)" /><Bar w="26%" tone="var(--text-faint)" /><Bar w="26%" tone="var(--text-faint)" /></span>
                      <span style={{ display: "block", height: "1px", background: "var(--border-strong)", marginLeft: "12%", width: "62%" }}></span>
                      <span style={{ display: "block", height: "1px", background: "var(--border-strong)", marginLeft: "34%", width: "48%" }}></span>
                    </div>
                  ) : (
                    <><Bar w="86%" tone="var(--text-faint)" /><Bar w="64%" tone="var(--border-strong)" /><Bar w="72%" tone="var(--border-strong)" /></>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StepPrAnalysis({ v, set }) {
  const c = v.cfg.summary_items;
  const upd = (k, field) => set("cfg", { ...v.cfg, summary_items: { ...c, [k]: { ...c[k], [field]: !c[k][field] } } });
  return (
    <>
      <h1 style={WIDE_TITLE}>What should be included in your PR summary?</h1>
      <p style={{ font: "var(--text-body-14)", color: "var(--text-muted)", marginBottom: "22px" }}>A PR summary is an overview of the changes, added as a comment or appended to the PR description.</p>
      <div style={PANEL}>
        {SUMMARY_ITEMS.map(x => (
          <div key={x.k} className="cn-config-row" style={ROW}>
            <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
              <span style={{ flex: 1 }}>
                <span style={{ display: "block", font: "var(--text-body-14)", color: "var(--text-primary)", marginBottom: "2px" }}>{x.h}</span>
                <span style={{ font: "var(--text-caption)", color: "var(--text-muted)" }}>{x.d}</span>
              </span>
              <Toggle on={c[x.k].on} onClick={() => upd(x.k, "on")} />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

/* A8 */
const DIFFS = [
  { f: "billing/settle.ts", p: "P0", tone: "var(--danger)", note: "Retry path re-enters the billing client." },
  { f: "webhooks/stripe.ts", p: "P1", tone: "var(--warning)", note: "Tenant filter missing on replay." },
  { f: "ui/table.tsx", p: "P2", tone: "var(--text-muted)", note: "Naming drifts from the module convention." }
];
function DiffPreview({ level }) {
  const keep = level === "Low" ? ["P0"] : level === "Medium" ? ["P0", "P1"] : ["P0", "P1", "P2"];
  const shown = DIFFS.filter(d => keep.includes(d.p));
  return (
    <div style={{ display: "grid", gap: "14px" }}>
      {shown.map((d, i) => (
        <div key={d.f} style={{ ...PV, animation: "cn-fade-up 460ms var(--ease-out) " + (i * 90) + "ms both" }}>
          <PvHead t={d.f} right={<span style={{ font: "10px/1 var(--font-mono)", color: "var(--btn-primary-ink)", background: d.tone, padding: "3px 7px", borderRadius: "4px" }}>{d.p}</span>} />
          <div>
            {[["01", "-", "var(--danger-surface)"], ["01", "+", "var(--success-surface)"], ["02", "+", "var(--success-surface)"]].map((l, k) => (
              <div key={k} style={{ display: "grid", gridTemplateColumns: "26px 14px 1fr", gap: "8px", alignItems: "center", padding: "5px 10px", background: l[2] }}>
                <span style={{ font: "9px/1 var(--font-mono)", color: "var(--text-muted)" }}>{l[0]}</span>
                <span style={{ font: "9px/1 var(--font-mono)", color: l[1] === "+" ? "var(--success)" : "var(--danger)" }}>{l[1]}</span>
                <Bar w={k === 1 ? "68%" : k === 2 ? "44%" : "58%"} tone={l[1] === "+" ? "var(--success-border)" : "var(--danger-border)"} />
              </div>
            ))}
            <div style={{ display: "grid", gap: "7px", padding: "12px", borderTop: "1px solid var(--surface-elevated)" }}>
              <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <LogoMark size={13} color="var(--logo-ink)" />
                <span style={{ font: "10px/1 var(--font-mono)", color: "var(--text-primary)" }}>codenerve</span>
              </span>
              <span style={{ font: "var(--text-caption)", color: "var(--text-secondary)" }}>{d.note}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function StepCommentScope({ v, set }) {
  const lvl = v.cfg.sensitivity;
  const note = lvl === "Low" ? "CodeNerve will comment only on blocking findings." : lvl === "Medium" ? "CodeNerve will comment on P2s less often." : "CodeNerve will comment on everything it finds, including style.";
  return (
    <>
      <h1 style={WIDE_TITLE}>What should CodeNerve comment on?</h1>
      <p style={{ font: "var(--text-body-14)", color: "var(--text-muted)", marginBottom: "22px" }}>Adjust what CodeNerve says when replying to code and highlighting issues.</p>
      <div className="cn-invite-panel" style={{ ...PANEL, padding: "20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <span style={{ flex: 1, minWidth: "180px" }}>
            <span style={{ display: "block", font: "var(--text-body-14)", color: "var(--text-primary)", marginBottom: "2px" }}>Comments sensitivity</span>
            <span style={{ font: "var(--text-caption)", color: "var(--text-muted)" }}>Severity threshold</span>
          </span>
          <span style={{ display: "flex", gap: "3px", padding: "3px", borderRadius: "6px", background: "var(--surface-subtle)", border: "1px solid var(--border)" }}>
            {["Low", "Medium", "High"].map(l => {
              const on = lvl === l;
              // Only High for now: it is the strictness the review engine was benchmarked at.
              const off = l !== "High";
              return (
                <button key={l} disabled={off} title={off ? "Coming soon" : undefined} onClick={() => set("cfg", { ...v.cfg, sensitivity: l })}
                  style={{ cursor: off ? "not-allowed" : "pointer", opacity: off ? 0.45 : 1,font: "var(--text-caption)", fontFamily: "var(--font-mono)", letterSpacing: "0.04em", textTransform: "uppercase", padding: "8px 16px", borderRadius: "7px", border: "none", color: on ? "var(--btn-primary-ink)" : "var(--text-secondary)", background: on ? "var(--btn-primary-bg)" : "transparent", transition: "background 240ms var(--ease-out), color 240ms var(--ease-out)" }}>{l}</button>
              );
            })}
          </span>
        </div>
        <p key={lvl} style={{ font: "var(--text-caption)", color: "var(--text-secondary)", marginTop: "16px", animation: "cn-fade-up 420ms var(--ease-out) both" }}>{note}</p>
      </div>
    </>
  );
}

/* A9 */
function TimelinePreview({ retrigger, updateDesc }) {
  const chips = [];
  chips.push({ t: "New commit detected", tone: "var(--border-strong)" });
  if (retrigger) chips.push({ t: "Re-analysed PR", tone: "var(--border-strong)" }, { t: "New review posted", tone: "var(--border-strong)" });
  if (retrigger && updateDesc) chips.push({ t: "PR description updated", tone: "var(--border-strong)" });
  return (
    <div style={{ display: "grid", gap: "12px" }}>
      <div style={PV}>
        <PvHead t="Pull request" right={<span style={{ font: "10px/1 var(--font-mono)", color: "var(--text-faint)" }}>#462</span>} />
        <div style={{ padding: "12px", display: "grid", gap: "7px" }}>
          <Bar w="52%" /><Bar w="78%" /><Bar w="64%" />
          {updateDesc && <span style={{ display: "grid", gap: "7px", paddingTop: "8px", borderTop: "1px dashed var(--border-strong)", animation: "cn-fade-up 420ms var(--ease-out) both" }}>
            <span style={{ font: "10px/1 var(--font-mono)", color: "var(--text-muted)" }}>appended by codenerve</span>
            <Bar w="70%" tone="var(--text-faint)" /><Bar w="46%" tone="var(--border-strong)" />
          </span>}
        </div>
      </div>
      {chips.map((c, i) => (
        <span key={c.t} style={{ justifySelf: "center", font: "10px/1 var(--font-mono)", letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-primary)", background: c.tone, padding: "6px 11px", borderRadius: "5px", animation: "cn-fade-up 460ms var(--ease-out) " + (i * 220) + "ms both" }}>{c.t}</span>
      ))}
      <div style={{ ...PV, padding: "12px", display: "grid", gap: "7px", animation: "cn-fade-up 460ms var(--ease-out) 120ms both" }}>
        <span style={{ display: "flex", alignItems: "baseline", gap: "9px" }}>
          <span style={{ font: "10px/1 var(--font-mono)", color: "var(--text-primary)" }}>pushed a commit</span>
          <span style={{ marginLeft: "auto", font: "10px/1 var(--font-mono)", color: "var(--text-faint)" }}>11:11</span>
        </span>
        <span style={{ font: "var(--text-caption)", fontFamily: "var(--font-mono)", color: "var(--text-secondary)" }}>a3f8c21 <span style={{ color: "var(--text-secondary)" }}>Fix token refresh</span></span>
      </div>
      {retrigger ? (
        <div style={{ ...PV, padding: "12px", display: "grid", gap: "8px", animation: "cn-fade-up 460ms var(--ease-out) 760ms both" }}>
          <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <LogoMark size={13} color="var(--logo-ink)" />
            <span style={{ font: "10px/1 var(--font-mono)", color: "var(--text-primary)" }}>codenerve</span>
            <span style={{ marginLeft: "auto", font: "10px/1 var(--font-mono)", color: "var(--text-faint)" }}>11:12</span>
          </span>
          <span style={{ font: "10px/1 var(--font-mono)", letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-faint)" }}>PR summary</span>
          <Bar w="88%" tone="var(--text-faint)" /><Bar w="72%" tone="var(--border-strong)" /><Bar w="80%" tone="var(--border-strong)" />
        </div>
      ) : (
        <span style={{ font: "var(--text-caption)", color: "var(--text-muted)", textAlign: "center", padding: "10px 0" }}>Review stays as it was, nothing re-runs on this commit.</span>
      )}
    </div>
  );
}

function StepCommitBehaviour({ v, set }) {
  const c = v.cfg;
  return (
    <>
      <h1 style={WIDE_TITLE}>How should CodeNerve respond to PR changes?</h1>
      <p style={{ font: "var(--text-body-14)", color: "var(--text-muted)", marginBottom: "22px" }}>Configure what happens when new commits land on a pull request that has already been reviewed.</p>
      <div style={PANEL}>
        {[["retrigger", "Retrigger review on new commits", "Re-analyse when new commits are pushed"], ["updateDesc", "Update PR description", "Append CodeNerve's summary and findings to the PR body"]].map(([k, h, d]) => (
          <div key={k} className="cn-config-row" style={{ ...ROW, gap: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
              <span style={{ flex: 1 }}>
                <span style={{ display: "block", font: "var(--text-body-14)", color: "var(--text-primary)", marginBottom: "2px" }}>{h}</span>
                <span style={{ font: "var(--text-caption)", color: "var(--text-muted)" }}>{d}</span>
              </span>
              <Toggle on={c[k]} onClick={() => set("cfg", { ...c, [k]: !c[k] })} />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

/* A10 */
function StepDevTools({ v, set }) {
  const c = v.cfg;
  return (
    <>
      <h1 style={WIDE_TITLE}>Integrate CodeNerve with your coding agents.</h1>
      <p style={{ font: "var(--text-body-14)", color: "var(--text-muted)", marginBottom: "22px" }}>Add a copy-paste prompt to each finding so you can fix it with your coding agent.</p>
      <div style={PANEL}>
        <div style={{ ...ROW, gap: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <span style={{ flex: 1 }}>
              <span style={{ display: "block", font: "var(--text-body-14)", color: "var(--text-primary)", marginBottom: "2px" }}>Prompt to fix with AI</span>
              <span style={{ font: "var(--text-caption)", color: "var(--text-muted)" }}>Include a copy-paste prompt for fixing each issue with your agent</span>
            </span>
            <Toggle on={c.promptFix} onClick={() => set("cfg", { ...c, promptFix: !c.promptFix })} />
          </div>
        </div>
      </div>
    </>
  );
}

function AgentPreview({ c }) {
  return (
    <div style={{ ...PV, animation: "cn-fade-up 520ms var(--ease-out) both" }}>
      <PvHead t="acme/webapp" />
      {[["01", "-", "var(--danger-surface)"], ["01", "+", "var(--success-surface)"], ["02", "+", "var(--success-surface)"]].map((l, k) => (
        <div key={k} style={{ display: "grid", gridTemplateColumns: "26px 14px 1fr", gap: "8px", alignItems: "center", padding: "5px 10px", background: l[2] }}>
          <span style={{ font: "9px/1 var(--font-mono)", color: "var(--text-muted)" }}>{l[0]}</span>
          <span style={{ font: "9px/1 var(--font-mono)", color: l[1] === "+" ? "var(--success)" : "var(--danger)" }}>{l[1]}</span>
          <Bar w={k === 1 ? "70%" : k === 2 ? "42%" : "56%"} tone={l[1] === "+" ? "var(--success-border)" : "var(--danger-border)"} />
        </div>
      ))}
      <div style={{ display: "grid", gap: "9px", padding: "12px", borderTop: "1px solid var(--surface-elevated)" }}>
        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <LogoMark size={13} color="var(--logo-ink)" />
          <span style={{ font: "10px/1 var(--font-mono)", color: "var(--text-primary)" }}>codenerve</span>
        </span>
        <Bar w="84%" tone="var(--text-faint)" /><Bar w="60%" tone="var(--border-strong)" />
        {c.promptFix && (
          <div style={{ background: "var(--surface-subtle)", border: "1px dashed var(--border-strong)", borderRadius: "6px", padding: "10px", display: "grid", gap: "6px", animation: "cn-fade-up 420ms var(--ease-out) both" }}>
            <span style={{ font: "10px/1 var(--font-mono)", letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-faint)" }}>prompt to fix</span>
            <span style={{ font: "10px/1.5 var(--font-mono)", color: "var(--text-secondary)" }}>"Guard the settle path with the shared idempotency helper…"</span>
          </div>
        )}
        {!c.promptFix && <span style={{ font: "var(--text-caption)", color: "var(--text-faint)" }}>No agent handoff, findings are text only.</span>}
      </div>
    </div>
  );
}

/* ---------- viewport fit ----------
   A laptop should never scroll the wizard. The vertical rhythm above is fluid, which
   covers the single-column steps; the two-column agent config is the one screen whose
   content genuinely exceeds a short laptop, so each of its columns takes its own
   scrollbar and the page itself stays put. Below 900px the columns stack and the page
   scrolls normally, which is what a phone wants. */
/* Keep configuration columns in normal flow; the wizard fits the whole screen. */
function FitStyles() {
  return <style>{`@media (min-width: 900px) { .cn-onboarding .cn-cfg-col { max-height: none; overflow: visible; } }`}</style>;
}

function StepAgentConfig({ tab, v, set }) {
  const Left = [StepPrAnalysis, StepCommentScope, StepCommitBehaviour, StepDevTools][tab];
  const right = tab === 0 ? <SummaryPreview c={v.cfg.summary_items} />
    : tab === 1 ? <DiffPreview level={v.cfg.sensitivity} />
    : tab === 2 ? <TimelinePreview retrigger={v.cfg.retrigger} updateDesc={v.cfg.updateDesc} />
    : <AgentPreview c={v.cfg} />;
  return (
    <div>
      <FitStyles />
      <TabStrip tab={tab} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: "clamp(24px,3.4vw,48px)", alignItems: "start" }}>
        <div className="cn-cfg-col"><Left v={v} set={set} /></div>
        <div className="cn-cfg-col" style={{ paddingTop: "6px" }}>{right}</div>
      </div>
    </div>
  );
}


/* ---------- batch 4: invite, trial, done (A11–A13) ---------- */

function StepInvite({ v, set }) {
  const invited = v.invites;
  const [draft, setDraft] = React.useState("");
  const ready = /@/.test(draft);
  const send = () => {
    if (!ready) return;
    const list = draft.split(",").map(x => x.trim()).filter(x => /@/.test(x));
    set("invites", invited.concat(list.filter(x => !invited.includes(x))));
    setDraft("");
  };
  return (
    <>
      <div style={{ display: "grid", justifyItems: "center", gap: "10px", marginBottom: "22px" }}>
        <LogoMark size={26} color="var(--logo-ink)" />
        <h1 style={{ font: "500 clamp(21px,2.1vw,28px)/1.15 var(--font-display)", letterSpacing: "-0.025em", color: "var(--text-primary)", textAlign: "center" }}>Invite your team</h1>
        <p style={{ font: "var(--text-body-14)", color: "var(--text-muted)", textAlign: "center" }}>Add team members to start using CodeNerve together.</p>
      </div>
      <div className="cn-invite-panel" style={{ ...PANEL, padding: "20px" }}>
        <span style={{ display: "block", font: "var(--text-label)", color: "var(--text-primary)", marginBottom: "18px" }}>Invite people to this team</span>
        <span style={LABEL}>Team</span>
        <div style={{ display: "flex", alignItems: "center", gap: "11px", padding: "12px 14px", borderRadius: "6px", background: "var(--surface-subtle)", border: "1px solid var(--border)", marginBottom: "18px" }}>
          <GitMark />
          <span>
            <span style={{ display: "block", font: "var(--text-body-14)", color: "var(--text-primary)" }}>{v.org || "Your organisation"}</span>
            <span style={{ font: "10px/1 var(--font-mono)", letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-faint)" }}>GitHub</span>
          </span>
        </div>
        <span style={LABEL}>Default role for invited people</span>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "18px" }}>
          {["Admin", "Member"].map(r => {
            const on = v.role === r;
            return (
              <button key={r} onClick={() => set("role", r)}
                style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", padding: "12px 14px", borderRadius: "6px", background: on ? "var(--surface-elevated)" : "var(--surface-subtle)", border: "1px solid " + (on ? "var(--border-stronger)" : "var(--border)"), transition: "background 240ms var(--ease-out), border-color 240ms var(--ease-out)" }}>
                <span style={{ display: "grid", placeItems: "center", width: "16px", height: "16px", borderRadius: "999rem", border: "1px solid " + (on ? "var(--control-on-border)" : "var(--border-strong)"), background: on ? "var(--control-on-bg)" : "transparent", transition: "background 240ms var(--ease-out)" }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="var(--control-on-ink)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" style={{ width: "9px", height: "9px", opacity: on ? 1 : 0, transition: "opacity 200ms var(--ease-out)" }}><path d="M20 6 9 17l-5-5" /></svg>
                </span>
                <span style={{ font: "var(--text-body-14)", color: on ? "var(--text-primary)" : "var(--text-secondary)" }}>{r}</span>
              </button>
            );
          })}
        </div>
        <span style={LABEL}>Email</span>
        <input value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.metaKey && !e.ctrlKey) { e.preventDefault(); send(); } }}
          placeholder="separate several with commas" style={INPUT} />
        {invited.length > 0 && (
          <div style={{ display: "flex", gap: "7px", flexWrap: "wrap", marginTop: "14px" }}>
            {invited.map(m => (
              <span key={m} style={{ display: "inline-flex", alignItems: "center", gap: "9px", font: "var(--text-caption)", fontFamily: "var(--font-mono)", color: "var(--text-primary)", background: "var(--surface-elevated)", border: "1px solid var(--border-strong)", padding: "7px 11px", borderRadius: "999rem", animation: "cn-pop 380ms var(--ease-out) both" }}>
                {m} <span style={{ color: "var(--text-faint)" }}>{v.role.toLowerCase()}</span>
                <button onClick={() => set("invites", invited.filter(x => x !== m))} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 0, font: "12px/1 var(--font-mono)" }}>×</button>
              </span>
            ))}
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "16px" }}>
          <button onClick={send} disabled={!ready}
            style={{ display: "inline-flex", alignItems: "center", gap: "8px", cursor: ready ? "pointer" : "not-allowed", font: "var(--text-label)", color: ready ? "var(--btn-primary-ink)" : "var(--text-faint)", background: ready ? "var(--btn-primary-bg)" : "var(--surface-elevated)", border: "1px solid " + (ready ? "var(--btn-primary-bg)" : "var(--border)"), borderRadius: "6px", padding: "10px 16px", transition: "background 240ms var(--ease-out), color 240ms var(--ease-out)" }}>
            <span>+</span> Send invite
          </button>
        </div>
      </div>
      <p style={{ font: "var(--text-caption)", color: "var(--text-faint)", textAlign: "center", marginTop: "16px" }}>You can skip this and invite people later from Settings → People.</p>
    </>
  );
}

/* A12, trial. The ring fills to fourteen days once. */
/* The prototype sold a trial: a 14-day countdown, an email before it ended, and an "Add
   payment method" button that set a boolean and then rendered a tick beside "Payment
   method added". There is no billing here, no trial field on an organisation and no card
   is ever taken, so all three were claims the product cannot keep -- and the button
   confirmed something that had not happened. What is true is that the beta is free. */
function StepTrial() {
  const R = 34, C = 2 * Math.PI * R;
  return (
    <div style={{ display: "grid", justifyItems: "center", gap: "18px", padding: "8px 0" }}>
      <div style={{ position: "relative", width: "92px", height: "92px" }}>
        <svg viewBox="0 0 92 92" style={{ width: "100%", height: "100%", transform: "rotate(-90deg)" }}>
          <circle cx="46" cy="46" r={R} fill="none" stroke="var(--border)" strokeWidth="4" />
          <circle cx="46" cy="46" r={R} fill="none" stroke="url(#cn-trial)" strokeWidth="4" strokeLinecap="round"
            strokeDasharray={C} style={{ strokeDashoffset: C, animation: "cn-ring 1400ms var(--ease-out) 320ms forwards" }} />
          <defs><linearGradient id="cn-trial" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="var(--text-secondary)" /><stop offset="100%" stopColor="var(--border-stronger)" /></linearGradient></defs>
        </svg>
        <span style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}>
          <LogoMark size={24} color="var(--logo-ink)" />
        </span>
      </div>
      <h1 style={{ font: "500 clamp(21px,2.1vw,28px)/1.15 var(--font-display)", letterSpacing: "-0.025em", color: "var(--text-primary)", textAlign: "center" }}>The private beta is free</h1>
      <p style={{ font: "var(--text-body-14)", color: "var(--text-muted)", textAlign: "center", maxWidth: "38ch" }}>There is nothing to pay and no payment method to add. We will tell you well before that changes, and never charge an account without asking first.</p>
      <span style={{ font: "var(--text-caption)", fontFamily: "var(--font-mono)", color: "var(--text-disabled)" }}>no card, no countdown</span>
    </div>
  );
}

/* A13, done. Hero strip reuses the wave band; counters count up in CSS steps. */
function CountTile({ n, label, tone, delay }) {
  return (
    <div style={{ padding: "18px 14px", borderRadius: "6px", background: tone.bg, border: "1px solid " + tone.br, textAlign: "center", animation: "cn-pop 520ms var(--ease-out) " + delay + "ms both" }}>
      <span style={{ display: "block", font: "500 clamp(24px,2.6vw,34px)/1 var(--font-display)", letterSpacing: "-0.03em", color: tone.fg, fontVariantNumeric: "tabular-nums" }}>{n}</span>
      <span style={{ display: "block", font: "10px/1.3 var(--font-mono)", letterSpacing: "0.07em", textTransform: "uppercase", color: tone.sub, marginTop: "7px" }}>{label}</span>
    </div>
  );
}

function StepDone({ v, github, stats, onFinish, onOpenDocs, busy }) {
  const repoCount = github?.counts?.submitted ?? github?.repositories?.length ?? 0;
  const rulesCount = stats?.ruleFiles ?? 0;
  return (
    <>
      <h1 style={{ font: "500 clamp(20px,2vw,26px)/1.2 var(--font-display)", letterSpacing: "-0.025em", color: "var(--text-primary)", textAlign: "center", marginBottom: "clamp(5px,0.9vh,8px)" }}>CodeNerve is ready to review your first pull request.</h1>
      <p style={{ font: "var(--text-body-14)", color: "var(--text-muted)", textAlign: "center", marginBottom: "22px" }}>Open a pull request on any connected repository and CodeNerve will review it automatically.</p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: "10px", marginBottom: "20px" }}>
        <CountTile n={repoCount} label="repos added" delay={120} tone={{ bg: "var(--surface-subtle)", br: "var(--border)", fg: "var(--text-primary)", sub: "var(--text-muted)" }} />
        <CountTile n={rulesCount} label="AI files found" delay={240} tone={{ bg: "var(--surface-subtle)", br: "var(--border)", fg: "var(--text-primary)", sub: "var(--text-muted)" }} />
        <CountTile n={v.invites.length} label="members invited" delay={360} tone={{ bg: "var(--surface-subtle)", br: "var(--border)", fg: "var(--text-primary)", sub: "var(--text-muted)" }} />
      </div>
      <div style={{ display: "grid", justifyItems: "center", gap: "14px" }}>
        <button disabled={busy} aria-busy={busy} onClick={onFinish} style={{ font: "var(--text-label)", color: "var(--btn-primary-ink)", background: "var(--btn-primary-bg)", border: "none", borderRadius: "6px", padding: "12px 24px", cursor: "pointer" }}>{busy ? "Saving..." : "Go to dashboard"}</button>
        <button onClick={onOpenDocs} style={{ font: "var(--text-label)", color: "var(--text-primary)", background: "var(--surface-elevated)", border: "1px solid var(--border-strong)", borderRadius: "6px", padding: "10px 16px", cursor: "pointer" }}>Check out the docs</button>
        <span style={{ font: "var(--text-caption)", fontFamily: "var(--font-mono)", color: "var(--text-disabled)" }}>{v.cfg.sensitivity.toLowerCase()} sensitivity · {v.cfg.retrigger ? "re-reviews on new commits" : "reviews once per PR"}</span>
      </div>
    </>
  );
}

/* ---------- wizard shell ---------- */

const STEPS = 13;
const STEP_DEFS = [
  {}, {}, {},
  { wide: true }, { wide: true }, { backdrop: "rules" },
  { wide: true }, { wide: true }, { wide: true }, { wide: true },
  {}, {}, {}
];

/* The product sequence, not the pre-launch one.

   The prototype opened on `EaDetails` -- "Apply for early access" -- and closed on
   `EaThanks`, "we will write back within a few working days". Both are the pre-launch
   variant, where the wizard *was* the application. Here the applying happened days
   earlier: the request was approved, a set-up link was mailed, and the person is signed
   in. So the first two steps are `StepName` and `StepOrg`, which the prototype carries
   for exactly this build and which the pre-launch one swapped out. Still thirteen.

   `StepOrg` also asks for the organisation handle, which `EaDetails` never did. The API
   requires one and it is the organisation's URL, so without this step it was being
   derived from the company name behind the user's back.

   Both pre-launch screens stay in the file, unreferenced: restoring that flow is a change
   to this array and nothing else. */
const DASH_STEP = STEPS - 1;

function OnboardingApp({ account, onExit, onFinish, onOpenDocs, onProgress, github, stats, initial, busy, error, handleError, onCheckHandle }) {
  /* `initial` is where the wizard was left: saved to the account on every step, or the
     draft written before the browser left for github.com. Resuming at the step it names
     is what makes signing out, closing the app or installing the App feel like a pause
     rather than an interruption that lost the answers. */
  const [step, setStep] = React.useState(initial?.step ?? 0);
  const openDocs = () => onOpenDocs && onOpenDocs();
  const [v, setV] = React.useState(initial?.answers ?? {
    name: account.name || "", email: account.email || "", org: account.org || "", handle: account.handle || "", site: "", size: "Just me", why: [], source: "Google",
    cfg: {
      summary_items: {
        summary: { on: true, collapsible: false, defaultOpen: false },
        sequence: { on: true, collapsible: true, defaultOpen: false },
        issues: { on: true, collapsible: false, defaultOpen: false },
        score: { on: true, collapsible: false, defaultOpen: false },
        outside: { on: true, collapsible: false, defaultOpen: false }
      },
      sensitivity: "High", retrigger: true, updateDesc: false, promptFix: false, agents: []
    },
    invites: [], role: "Member"
  });
  const set = (k, val) => {
    /* The handle follows the organisation name until the user types one of their own:
       a handle that is empty or still the suggestion for the current name is untouched. */
    if (k === "org" && (!v.handle || v.handle === toHandle(v.org))) {
      const handle = toHandle(val);
      setV(x => ({ ...x, org: val, handle }));
      if (onCheckHandle) onCheckHandle(handle);
      return;
    }
    setV(x => ({ ...x, [k]: val }));
    if (k === "handle" && onCheckHandle) onCheckHandle(val);
  };
  const valid = [
    () => v.name.trim().length > 1,
    () => v.org.trim().length > 1 && /^[a-z0-9][a-z0-9-]{0,62}$/.test(v.handle) && !handleError,
    () => true,
    /* Repositories: the rest of the wizard indexes and configures them, so it waits for one. */
    () => Boolean(github?.connected),
    () => true,
    () => true, () => true, () => true, () => true, () => true,
    () => true, () => true, () => true
  ];
  /* Reported on every Next and Back, not every keystroke: the screen is what has to
     survive a sign out, and a save per character would be a request per character. */
  React.useEffect(() => {
    if (onProgress) onProgress(step, v);
  }, [step]);
  const last = step >= STEPS - 1;
  const canNext = valid[step] ? valid[step]() : false;
  const next = () => {
    if (!canNext || busy) return;
    /* The last step is Finish. It saves and the caller routes onward; nothing advances
       here, because a confirmation shown before the save returned would be reporting a
       configuration the server may have rejected. */
    if (step === DASH_STEP) { onFinish(v); return; }
    if (last) return;
    setStep(s => s + 1);
  };
  React.useEffect(() => {
    const k = e => {
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); e.shiftKey ? !busy && setStep(s => Math.max(0, s - 1)) : next(); }
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  });
  const AGENT_TAB = { 6: 0, 7: 1, 8: 2, 9: 3 };
  /* An element, not a component reference. Building the body as `<Body .../>` out of a
     freshly created arrow handed React a new component *type* on every render, so every
     `set()` unmounted the step and remounted it, discarding any state it held locally. */
  const Step = [StepName, StepOrg, StepWhy, StepRepos, StepIndexing, StepRules][step];
  const body = step >= 6 && step <= 9
    ? <StepAgentConfig tab={AGENT_TAB[step]} v={v} set={set} />
    : step === 10
      ? <StepInvite v={v} set={set} />
      : step === 11
        ? <StepTrial />
        : step === DASH_STEP
          ? <StepDone busy={busy} v={v} github={github} stats={stats} onFinish={() => onFinish(v)} onOpenDocs={openDocs} />
          : <Step busy={busy} v={v} set={set} github={github} handleError={handleError} onConnect={() => github.onConnect(step, v)} />;
  const viewportRef = React.useRef(null);
  const contentRef = React.useRef(null);
  const [contentZoom, setContentZoom] = React.useState(1);
  const useClientLayoutEffect = typeof window === "undefined" ? React.useEffect : React.useLayoutEffect;
  useClientLayoutEffect(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport || !content) return;
    const fit = () => {
      const style = getComputedStyle(viewport);
      const available = viewport.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom) - 12;
      const ratio = Math.min(1, available / content.offsetHeight);
      // Keep small screens and heavily zoomed views readable, with a scrolling fallback.
      const next = window.innerWidth >= 900 && ratio >= 0.8 ? Math.floor(ratio * 100) / 100 : 1;
      setContentZoom(current => Math.abs(current - next) > 0.005 ? next : current);
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(viewport);
    observer.observe(content);
    window.addEventListener("resize", fit);
    return () => { observer.disconnect(); window.removeEventListener("resize", fit); };
  }, [step]);
  const def = STEP_DEFS[step] || {};
  return (
    <div className="cn-onboarding" data-step={step + 1} style={{ "--radius-btn": "5px", "--radius-panel": "6px", position: "fixed", inset: 0, background: "var(--bg)", display: "grid", gridTemplateRows: "minmax(0,1fr) auto", overflow: "hidden" }}>
      <style>{`
        @keyframes cn-step-appear { from { opacity: 0; } to { opacity: 1; } }
        @media (min-width: 900px) {
          .cn-onboarding[data-step="3"] .cn-step-content,
          .cn-onboarding[data-step="11"] .cn-step-content { width: min(580px,100%) !important; padding: 18px 24px !important; box-sizing: border-box; }
          .cn-onboarding[data-step="7"] .cn-config-row { grid-template-columns: minmax(0,1fr); gap: 2px !important; }
          .cn-onboarding[data-step="11"] .cn-step-content > p { margin-top: 8px !important; }
          .cn-onboarding[data-step="11"] .cn-invite-panel { padding: 10px !important; }
          .cn-onboarding[data-step="11"] .cn-invite-panel > div { padding-top: 6px !important; padding-bottom: 6px !important; margin-bottom: 6px !important; }
          .cn-onboarding[data-step="11"] .cn-step-content > div:has(h1) p { margin: 0 !important; }
          .cn-onboarding[data-step="7"] .cn-summary-preview > div:last-child { padding: 8px !important; gap: 6px !important; }
          .cn-onboarding[data-step="7"] .cn-summary-preview > div:last-child > div > div { padding: 7px 10px !important; gap: 5px !important; }
          .cn-challenges { grid-template-columns: repeat(2,minmax(0,1fr)); }
          .cn-invite-panel { padding: 14px !important; }
          .cn-invite-panel > div, .cn-invite-panel > span { margin-bottom: 10px !important; }
          .cn-onboarding[data-step="11"] .cn-step-content > div:has(h1) { gap: 6px !important; margin-bottom: 12px !important; }
          .cn-onboarding[data-step="7"] .cn-cfg-col,
          .cn-onboarding[data-step="9"] .cn-cfg-col { max-height: none; overflow-y: visible; }
          .cn-onboarding[data-step="7"] .cn-config-row,
          .cn-onboarding[data-step="9"] .cn-config-row { padding: 10px 14px !important; }
          .cn-onboarding[data-step="7"] .cn-cfg-col h1,
          .cn-onboarding[data-step="9"] .cn-cfg-col h1 { font-size: 24px !important; }
          .cn-onboarding[data-step="7"] .cn-cfg-col > p,
          .cn-onboarding[data-step="9"] .cn-cfg-col > p { margin-bottom: 12px !important; }
          .cn-onboarding[data-step="11"] div.cn-step-content { padding: 6px 24px !important; }
          .cn-onboarding[data-step="11"] h1 { margin: 0 !important; }
          .cn-onboarding[data-step="11"] .cn-step-content > div:first-of-type,
          .cn-onboarding[data-step="11"] .cn-step-content > div:nth-of-type(2) { margin-bottom: 8px !important; }
        }
      `}</style>
      <style>{`.cn-onboarding button:not([role="switch"]), .cn-onboarding input, .cn-onboarding select { border-radius: 5px !important; } .cn-onboarding button:disabled { cursor: not-allowed; opacity: .6; }`}</style>
      <div ref={viewportRef} className="cn-step-viewport" style={{ minHeight: 0, position: "relative", display: "flex", flexDirection: "column", alignItems: "center", overflowY: "auto", overflowX: "hidden", padding: "clamp(10px,2.2vh,44px) 16px" }}>
        <AppBackdrop shift={step % 4} dense={def.wide} step={step} />

        {/* One column: chrome and body share a width and a padding box, so the header,
            the progress rule and the content all align on every step. */}
        <div key={step} ref={contentRef} className="cn-step-content" style={{ zoom: contentZoom, flexShrink: 0, position: "relative", minHeight: "min-content", width: def.wide ? "min(1180px,100%)" : "min(438px,100%)", ...(def.wide ? {} : { background: "linear-gradient(var(--surface-elevated),var(--surface))", border: "1px solid var(--border)", borderRadius: "6px", boxShadow: "0 60px 120px -60px #000, inset 0 1px 0 #ffffff12" }), margin: "auto 0", padding: def.wide ? 0 : "clamp(18px,2.4vh,28px) clamp(20px,3.2vw,32px) clamp(20px,2.8vh,32px)", animation: "cn-step-appear 240ms ease-out both" }}>
          {!def.wide && <span aria-hidden="true" style={{ position: "absolute", left: "22%", right: "22%", top: 0, height: "1px", background: "linear-gradient(90deg,transparent,#ffffff2e,transparent)" }}></span>}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", marginBottom: "clamp(8px,1.3vh,14px)" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
              <LogoMark size={16} color="var(--logo-ink)" />
              <span style={{ font: "500 14px/1.2 var(--font-display)", letterSpacing: "var(--tracking-display)", color: "var(--logo-ink)" }}>CodeNerve AI</span>
            </span>
            <span style={{ font: "var(--text-caption)", fontFamily: "var(--font-mono)", color: "var(--text-muted)", fontVariantNumeric: "tabular-nums" }}>{String(step + 1).padStart(2, "0")} / {STEPS}</span>
          </div>
          <div style={{ display: "flex", gap: "4px", marginBottom: "clamp(12px,2.2vh,24px)" }}>
            {Array.from({ length: STEPS }).map((_, i) => (
              <span key={i} style={{ flex: 1, height: "2px", borderRadius: "999rem", background: i === step ? "var(--text-primary)" : i < step ? "var(--border-stronger)" : "var(--border)", transition: "background 420ms var(--ease-out)" }}></span>
            ))}
          </div>
          {error && <div role="alert" style={{ padding: "12px 16px", marginBottom: "16px", border: "1px solid var(--danger)", borderRadius: "6px", color: "var(--danger)", background: "var(--bg)", font: "var(--text-body-14)" }}>{error} Please retry after resolving this issue.</div>}
          {body}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: "16px", padding: "clamp(9px,1.5vh,16px) clamp(16px,3vw,32px)", borderTop: "1px solid var(--border)", background: "var(--bg)" }}>
        <button onClick={onExit} style={{ justifySelf: "start", background: "transparent", border: "none", cursor: "pointer", font: "var(--text-body-14)", color: "var(--text-faint)" }}>Log out</button>
        <div style={{ display: "flex", gap: "10px", justifySelf: "center" }}>
          {step > 0 && (
            <button disabled={busy} onClick={() => setStep(s => s - 1)} style={{ minWidth: "min(220px,32vw)", height: "44px", borderRadius: "6px", cursor: "pointer", font: "var(--text-label)", color: "var(--text-primary)", background: "var(--surface-elevated)", border: "1px solid var(--border-strong)" }}>
              Back
            </button>
          )}
          {last ? (
            <button onClick={() => onFinish(v)} disabled={busy} style={{ position: "relative", overflow: "hidden", minWidth: "min(300px,44vw)", height: "44px", borderRadius: "6px", border: "none", cursor: busy ? "not-allowed" : "pointer", font: "var(--text-label)", color: "var(--btn-primary-ink)", background: "var(--btn-primary-bg)" }}>
              {busy ? "Working…" : "Finish"}
            </button>
          ) : (
          <button onClick={next} disabled={!canNext || busy}
            style={{ position: "relative", overflow: "hidden", minWidth: "min(300px,44vw)", height: "44px", borderRadius: "6px", cursor: canNext && !busy ? "pointer" : "not-allowed", font: "var(--text-label)", color: canNext && !busy ? "var(--btn-primary-ink)" : "var(--text-faint)", background: canNext && !busy ? "var(--btn-primary-bg)" : "var(--surface-elevated)", border: "1px solid " + (canNext && !busy ? "var(--btn-primary-bg)" : "var(--border)"), transition: "background 280ms var(--ease-out), color 280ms var(--ease-out)" }}>
            {canNext && !busy && <span aria-hidden="true" style={{ position: "absolute", inset: 0, background: "linear-gradient(100deg,#ffffff00 30%,#ffffff33 50%,#ffffff00 70%)", animation: "cn-sheen 2.6s linear infinite" }}></span>}
            {busy ? "Working…" : step === DASH_STEP ? "Finish" : "Next"}
          </button>
          )}
        </div>
        <span style={{ justifySelf: "end", font: "var(--text-caption)", fontFamily: "var(--font-mono)", color: error ? "var(--danger)" : "var(--text-faint)", maxWidth: "34ch", textAlign: "right" }}>{error || ""}</span>
      </div>
    </div>
  );
}

export { OnboardingApp, indexErrorText, toHandle };

